import { html, svg, type TemplateResult } from "lit-html";

/**
 * Inline SVG icon set — single-stroke, currentColor.
 *
 * Replaces the emoji icons we used through phase 2: search 🔍,
 * iMessage 💬, copy 📋, phone detect 📱. Emojis render at the OS's
 * mercy (Apple Color Emoji is fine on Safari/iOS but looks loud and
 * misaligned against the rest of the chrome). These icons inherit
 * font size + color so they line up with the surrounding text.
 *
 * Sized to 1em so font-size on the container controls the visual.
 * Defaults to a 1.5 stroke (Heroicons outline weight).
 */

interface Opts { size?: number | string; }

function wrap(path: TemplateResult, opts: Opts = {}): TemplateResult {
  const s = opts.size ?? "1em";
  return html`
    <svg class="icon" xmlns="http://www.w3.org/2000/svg" width=${s} height=${s}
         viewBox="0 0 24 24" fill="none" stroke="currentColor"
         stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"
         aria-hidden="true">
      ${path}
    </svg>
  `;
}

export const iconSearch = (opts?: Opts) =>
  wrap(svg`<circle cx="11" cy="11" r="7"></circle><path d="m20 20-3.5-3.5"></path>`, opts);

export const iconMessage = (opts?: Opts) =>
  wrap(svg`<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>`, opts);

export const iconClipboard = (opts?: Opts) =>
  wrap(svg`<rect x="8" y="3" width="8" height="4" rx="1"></rect><path d="M16 5h2a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2"></path>`, opts);

export const iconCheck = (opts?: Opts) =>
  wrap(svg`<path d="M20 6 9 17l-5-5"></path>`, opts);

export const iconPhone = (opts?: Opts) =>
  wrap(svg`<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>`, opts);

export const iconClose = (opts?: Opts) =>
  wrap(svg`<path d="M18 6 6 18M6 6l12 12"></path>`, opts);

export const iconTrash = (opts?: Opts) =>
  wrap(svg`<path d="M3 6h18"></path><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line>`, opts);

export const iconRestore = (opts?: Opts) =>
  wrap(svg`<path d="M3 12a9 9 0 1 0 3-6.7"></path><path d="M3 4v5h5"></path>`, opts);

export const iconSoundOn = (opts?: Opts) =>
  wrap(svg`<polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path><path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>`, opts);

export const iconSoundOff = (opts?: Opts) =>
  wrap(svg`<polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><line x1="22" y1="9" x2="16" y2="15"></line><line x1="16" y1="9" x2="22" y2="15"></line>`, opts);

export const iconBell = (opts?: Opts) =>
  wrap(svg`<path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"></path><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"></path>`, opts);
